import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { 
  FileCheck, 
  FileX, 
  Clock, 
  AlertTriangle, 
  TrendingUp,
  ArrowRight,
  Users,
  Shield,
  RefreshCw
} from 'lucide-react';
import { cn, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#0F172A', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentCases, setRecentCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, casesRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`),
        axios.get(`${API_URL}/api/cases?limit=5`)
      ]);
      setStats(statsRes.data);
      setRecentCases(casesRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const statusChartData = stats?.cases_by_status 
    ? Object.entries(stats.cases_by_status).map(([name, value]) => ({
        name: name.replace('_', ' '),
        value
      }))
    : [];

  const countryChartData = stats?.cases_by_country
    ? Object.entries(stats.cases_by_country).map(([name, value]) => ({
        name,
        cases: value
      }))
    : [];

  return (
    <div className="p-6 space-y-6" data-testid="dashboard">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of verification operations</p>
        </div>
        <div className="flex gap-2">
          <PageHelp pageKey="dashboard" />
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending Cases</p>
                <p className="text-3xl font-bold mt-1">{stats?.pending_cases || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {stats?.high_risk_cases || 0} high risk
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Approved Today</p>
                <p className="text-3xl font-bold mt-1 text-emerald-600">{stats?.approved_today || 0}</p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-lg">
                <FileCheck className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Rejected Today</p>
                <p className="text-3xl font-bold mt-1 text-red-600">{stats?.rejected_today || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <FileX className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">SLA Compliance</p>
                <p className="text-3xl font-bold mt-1">{stats?.sla_compliance_rate?.toFixed(1) || 0}%</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Avg {stats?.avg_processing_time_hours?.toFixed(1) || 0}h processing
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Alert Cards */}
      {(stats?.high_risk_cases > 0 || stats?.fraud_flagged_cases > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stats?.high_risk_cases > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-amber-900">{stats.high_risk_cases} High Risk Cases</p>
                  <p className="text-sm text-amber-700">Require immediate attention</p>
                </div>
                <Button variant="outline" size="sm" asChild className="border-amber-300">
                  <Link to="/cases?risk_band=high">Review</Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cases by Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Cases by Status</CardTitle>
          </CardHeader>
          <CardContent>
            {statusChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cases by Country */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Cases by Country</CardTitle>
          </CardHeader>
          <CardContent>
            {countryChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={countryChartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="cases" fill="#0F172A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Cases */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Recent Cases</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/cases" className="flex items-center gap-1">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {recentCases.length > 0 ? (
            <div className="space-y-3">
              {recentCases.map((caseItem) => (
                <Link 
                  key={caseItem.id} 
                  to={`/cases/${caseItem.id}`}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-medium">{caseItem.applicant_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {caseItem.country} • {caseItem.channel}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className={cn(getStatusColor(caseItem.status))}>
                      {caseItem.status.replace('_', ' ')}
                    </Badge>
                    {caseItem.is_overdue && (
                      <Badge variant="destructive" className="text-xs">
                        Overdue
                      </Badge>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No cases yet</p>
              <p className="text-sm mt-1">Cases will appear here when created via invitations</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Dashboard;
